import { type MessageInitShape, create } from '@bufbuild/protobuf'
import { Code, ConnectError } from '@connectrpc/connect'
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BiddingSpotBid,
  BiddingSpotBidFeeAutoConsiderInputSchema,
  BiddingSpotBidInput,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import {
  BiddingSpotBidInputSchema,
  BiddingSpotBidOrigin,
  BiddingSpotOrderMethod,
  SpotBidWriteBlockedReason,
  SpotBidWriteBlockedReasonSchema,
} from '@ebalance/proto/bffuser/v1/bidding_spot_model_pb'
import { DecimalSchema } from '@ebalance/proto/google/type/decimal_pb'
import type { TFunction } from 'i18next'

import {
  type AppErrorInfoReasonEntry,
  readAppErrorInfo,
  resolveAppErrorInfoMessage,
} from '~/libs/appErrorInfo'
import type { FeeAutoConsiderConfirmed } from '~/models/feeAutoConsiderPreview'
import { volumeTotalLimitMessage } from '~/models/volumeTotalLimitMessage'
import { ApprovalFlowUnresolvedError } from '~/pages/SpotBidding/models/approvalModeResolve'
import type {
  BulkGeneratedBid,
  BulkVolumeMode,
} from '~/pages/SpotBidding/models/bulkCreate'
import type {
  IndividualBidFormInput,
  SpotBidCreateFormInput,
} from '~/pages/SpotBidding/models/schema'
import type {
  SpotBidIssuerType,
  SpotBidRow,
} from '~/pages/SpotBidding/models/spotBidRow'

// フォーム側の `orderMethod` は zod schema 由来の生 number (= proto enum 型を
// 付けていない、 `direction`/`areaCode` 等の他 enum field と同じ規約)。 enum member と
// 直接比較すると @typescript-eslint/no-unsafe-enum-comparison に触れるため number 化する
// (`Supply48Data/models/supply48QueryParams.ts` と同じ回避)。
const ORDER_METHOD_MARKET: number = BiddingSpotOrderMethod.MARKET

/** 発注主体 string union → LicenseType enum (= 編集フォーム初期値用)。 */
export const LICENSE_TYPE_BY_ISSUER: Record<SpotBidIssuerType, LicenseType> = {
  generation: LicenseType.GENERATION,
  retail: LicenseType.RETAIL,
  dr: LicenseType.DR,
  selfwheeling: LicenseType.SELF_WHEELING,
  unknown: LicenseType.UNSPECIFIED,
}

/**
 * 発注主体 string union → LicenseType。 発注主体を持たない行 / entry
 * (= `issuerType` が undefined。 #6094) は **既定値で埋めず** UNSPECIFIED に落とす。
 *
 * UNSPECIFIED はフォーム schema (`issuerType` の `min(1)`) と backend の
 * protovalidate (`BiddingSpotBidInput.issuer_type` の `not_in: [0, 3]`) の双方が
 * 弾くため、 発電などに無言で寄せるより安全 (= ユーザーに選び直させる / 400 で気づける)。
 */
const licenseTypeFromIssuer = (
  issuer: SpotBidIssuerType | undefined,
): LicenseType =>
  issuer !== undefined
    ? LICENSE_TYPE_BY_ISSUER[issuer]
    : LicenseType.UNSPECIFIED

/**
 * 表示行 `SpotBidRow` を個別作成フォーム入力に戻す (= 行編集の初期値)。
 * 行・フォームとも数量は MW (AC-BSP-053) なので換算しない。
 *
 * 発注主体区分は札の区分をそのまま選択値にする (発注主体を持たない行は UNSPECIFIED)。
 *
 * `orderMethod` は `undefined` / `UNSPECIFIED` (= order_method を送らない旧クライアント
 * が作った札) を指値 (LIMIT) に倒す。 [[isMarketOrderRow]] と同じ境界解決 (backend
 * converter と同じ規約)。
 */
export function bidRowToForm(row: SpotBidRow): IndividualBidFormInput {
  return {
    direction: row.direction,
    timeCode: row.timeCode,
    areaCode: row.areaCode,
    issuerType: licenseTypeFromIssuer(row.issuerType),
    priceYenPerKwh: Number(row.priceYenPerKwh),
    volumeMw: Number(row.volumeMwh),
    orderMethod:
      row.orderMethod === BiddingSpotOrderMethod.MARKET
        ? BiddingSpotOrderMethod.MARKET
        : BiddingSpotOrderMethod.LIMIT,
    memoJepx: row.memoJepx,
    memoFree: row.memoFree,
  }
}

/**
 * 個別作成が backend のプレビューで見ていた手数料自動考慮の状態 (AC-BSP-004b)。 付いた札は、 入力欄の価格を
 * 希望価格として送り、 入札価格は backend が算出する (#8109)。 付かない札 (手数料単価を使えず OFF として扱う
 * 画面・成行・画面で入札価格を算出している経路) は入力欄の価格を入札価格として送る。
 */
export type SpotBidFeeAutoConsiderEntry = {
  feeAutoConsider?: FeeAutoConsiderConfirmed
}

/** コマ選択の個別作成 ([[SpotBidCreateModal]]) が登録する札。 */
export type SpotBidCreateSubmission = SpotBidCreateFormInput &
  SpotBidFeeAutoConsiderEntry

/**
 * 価格の入力は入札価格そのものか希望価格 + 状態のどちらか 1 つ (proto `BiddingSpotBidInput` /
 * `BiddingBlockBidInput` の CEL)。 戻り値は両 message の同名 field に代入できる形。
 */
export const priceInputOf = (
  isMarket: boolean,
  entryPriceYenPerKwh: string,
  fee: FeeAutoConsiderConfirmed | undefined,
): {
  priceYenPerKwh?: MessageInitShape<typeof DecimalSchema>
  feeAutoConsider?: MessageInitShape<
    typeof BiddingSpotBidFeeAutoConsiderInputSchema
  >
} =>
  fee !== undefined && !isMarket
    ? {
        feeAutoConsider: {
          desiredPriceYenPerKwh: { value: entryPriceYenPerKwh },
          enabled: fee.enabled,
          feeYenPerKwh:
            fee.feeYenPerKwh !== undefined
              ? { value: fee.feeYenPerKwh }
              : undefined,
        },
      }
    : {
        priceYenPerKwh: create(DecimalSchema, {
          value: isMarket ? '0' : entryPriceYenPerKwh,
        }),
      }

/** 空文字を undefined に畳む (= 空メモ欄を proto に載せない)。 */
const emptyToUndefined = (v: string | undefined): string | undefined =>
  v !== undefined && v !== '' ? v : undefined

/**
 * 個別作成フォーム入力を proto の書込用 `BiddingSpotBidInput` に変換する。
 * 数量はフォーム入力・proto・DB・eSLJ 送信のすべてが MW なので換算しない
 * (issue #4285。 proto field 名 `volume_mwh` は旧 spec 時代の歴史的経緯)。
 *
 * `orderMethod` はフォーム自身が持つ (AC-BSP-004a: 個別作成モーダルに価格種別
 * 〈指値 / 成行〉選択を追加)。 成行選択時は価格入力欄を無効化しているが、 disabled
 * input の値が何らかの理由で残っていても送信直前に強制的に "0" へ落とす
 * (backend `SpotBid` entity は「成行は price=0」 を不変条件として検証する、
 * `go/internal/backend/domain/entity/bidding/spot_bid.go`)。
 *
 * `origin` は呼び出し元が決める (AC-BSP-103)。 個別作成・複製・再入札は人が数量を
 * 入力する新しい札なので手動起票、 既存札の編集は [[editedBidOrigin]] の値を渡す。
 * 省略不可にしてあるのは、 編集で落とすと自動起票の札が手動起票に変わるため。
 */
export function formToBidInput(
  form: IndividualBidFormInput & SpotBidFeeAutoConsiderEntry,
  origin: BiddingSpotBidOrigin,
): BiddingSpotBidInput {
  const isMarket = form.orderMethod === ORDER_METHOD_MARKET
  return create(BiddingSpotBidInputSchema, {
    areaCode: form.areaCode as AreaCode,
    timeCode: form.timeCode,
    direction: form.direction as Side,
    orderMethod: form.orderMethod as BiddingSpotOrderMethod,
    origin,
    ...priceInputOf(
      isMarket,
      String(form.priceYenPerKwh),
      form.feeAutoConsider,
    ),
    volumeMwh: create(DecimalSchema, {
      value: String(form.volumeMw),
    }),
    issuerType: form.issuerType as LicenseType,
    memoJepx: emptyToUndefined(form.memoJepx),
    memoFree: emptyToUndefined(form.memoFree),
  })
}

/**
 * コマ枠クリックの作成フォーム入力 `SpotBidCreateFormInput` を書込用
 * `BiddingSpotBidInput` に変換する。 価格・数量とも form の値をそのまま Decimal value に
 * 流す (数量は MW 建てで一貫、 issue #4285)。 発注主体は form の licenseType を使う。
 *
 * `orderMethod` は [[formToBidInput]] と同じく成行選択時に価格を強制的に "0" へ
 * 落とす (AC-BSP-004a、 backend `SpotBid` entity の不変条件)。
 * `origin` は手動起票 (= 人が数量を入力する新しい札)。
 */
export function spotCreateFormToBidInput(
  form: SpotBidCreateSubmission,
): BiddingSpotBidInput {
  const isMarket = form.orderMethod === ORDER_METHOD_MARKET
  return create(BiddingSpotBidInputSchema, {
    areaCode: form.areaCode as AreaCode,
    timeCode: form.timeCode,
    direction: form.direction as Side,
    orderMethod: form.orderMethod as BiddingSpotOrderMethod,
    origin: BiddingSpotBidOrigin.MANUAL,
    ...priceInputOf(isMarket, form.priceYenPerKwh, form.feeAutoConsider),
    volumeMwh: create(DecimalSchema, { value: String(form.volumeMw) }),
    issuerType: form.licenseType as LicenseType,
    memoJepx: emptyToUndefined(form.memoJepx),
    memoFree: emptyToUndefined(form.memoFree),
  })
}

/**
 * 既存の表示用 `BiddingSpotBid` を書込用 `BiddingSpotBidInput` に逆変換する。
 * Update は全置換のため、 既存札を input に戻して新札と一緒に送る。
 *
 * `orderMethod` も戻す。 CSV 取込で作られた成行札 (`spot-bid-normal-csv-import.md`
 * AC-SPOT-006) が同じ承認依頼に混ざっている場合、 ここで落とすと全置換 Update の
 * たびに指値へ格下げされ、 価格を持たない札が 0 円指値として再構築されてしまう。
 *
 * `origin` (起票由来、 AC-BSP-103) も札に保存された値を戻す。 Update は札を全置換し
 * backend は送り直した札を永続札と結びつけられないため、 起票由来は書込側が往復させて
 * 申告する。 落とすと backend 境界で手動起票に解決され、 自動起票の札が他の札の
 * 編集のたびに手動起票へ変わる。
 */
export function spotBidToInput(b: BiddingSpotBid): BiddingSpotBidInput {
  return create(BiddingSpotBidInputSchema, {
    areaCode: b.areaCode,
    timeCode: b.timeCode,
    direction: b.direction,
    orderMethod: b.orderMethod,
    origin: b.origin,
    priceYenPerKwh: b.priceYenPerKwh,
    volumeMwh: b.volumeMwh,
    issuerType: b.issuerType,
    memoJepx: b.memoJepx,
    memoFree: b.memoFree,
  })
}

/**
 * 既存札の個別編集で送る起票由来を決める。 数量が元と同じなら元の起票由来を保ち、
 * 数量を手で直したら手動起票に戻す (mock-app/docs/domain-knowledge.md §4「自動札の
 * 数量を人が手で編集したら手動扱いに戻す」)。 元の起票由来が無い札も手動起票。
 * 数量は Decimal 文字列 ("60.0") とフォームの number (60) を数値として比べる。
 */
export function editedBidOrigin(
  originalOrigin: BiddingSpotBidOrigin | undefined,
  originalVolumeMw: string | undefined,
  editedVolumeMw: number,
): BiddingSpotBidOrigin {
  return originalOrigin !== undefined &&
    Number(originalVolumeMw) === editedVolumeMw
    ? originalOrigin
    : BiddingSpotBidOrigin.MANUAL
}

/**
 * 手動起票と同じ扱いの起票由来。 画面の 上限数量(1札) の事前チェック (AC-BSP-103) を
 * 当てる側を列挙する。 最終判定は backend なので、 ここに無い値 (自動起票) は画面で
 * 先に止めない。
 */
const MANUAL_EQUIVALENT_ORIGINS: ReadonlySet<BiddingSpotBidOrigin> = new Set([
  BiddingSpotBidOrigin.UNSPECIFIED,
  BiddingSpotBidOrigin.MANUAL,
  BiddingSpotBidOrigin.BULK_FIXED_VOLUME,
])

/** 送る札の起票由来に 上限数量(1札) の画面の事前チェックを当てるか (AC-BSP-103)。 */
export const appliesPerBidVolumeLimit = (
  origin: BiddingSpotBidOrigin,
): boolean => MANUAL_EQUIVALENT_ORIGINS.has(origin)

/**
 * 一括作成の数量入力モードを、 書込 contract の起票由来 (`origin`) に対応付ける。
 *
 * 目標カバー率モード (AC-BSP-005a) の札だけが 自動起票 で、 上限数量(1札)
 * (AC-BSP-103 / `bid-limits.md` 1-2) の適用対象から外れる。 数量指定モード
 * (AC-BSP-005c) は数量を人が入力するので spec の除外規定に含まれず、 個別作成と
 * 同じく適用対象のまま。 判定するのは backend で、 frontend は由来を申告するだけ。
 */
export const BULK_ORIGIN_BY_VOLUME_MODE: Record<
  BulkVolumeMode,
  BiddingSpotBidOrigin
> = {
  coverage: BiddingSpotBidOrigin.BULK_TARGET_COVERAGE,
  fixed: BiddingSpotBidOrigin.BULK_FIXED_VOLUME,
}

/**
 * 一括作成の生成札 `BulkGeneratedBid` を書込用 `BiddingSpotBidInput` に変換する (AC-005)。
 * `issuerType` はモーダルで選んだ発注主体区分を全生成札へ同一適用する。 以前は発電に無言固定していた (issue #5366)。
 * memos も同様にモーダルで 1 回入力した値を全生成札へ同一適用する (= 排出係数紐付けの
 * 判別コメント (memoJepx、 AC-BSP-086) + メモ（自由記述、 memoFree))。
 *
 * `origin` は数量入力モード由来の起票由来 ([[BULK_ORIGIN_BY_VOLUME_MODE]])。
 * 省略不可にしてあるのは、 落とすと UNSPECIFIED (= backend 境界で手動起票に解決)
 * になり、 目標カバー率モードの札が 上限数量(1札) で保存ブロックされるため
 * (issue #8165 の回帰)。
 */
export function bulkGeneratedToBidInput(
  bid: BulkGeneratedBid,
  issuerType: LicenseType,
  origin: BiddingSpotBidOrigin,
  memos?: { memoJepx?: string; memoFree?: string },
): BiddingSpotBidInput {
  return create(BiddingSpotBidInputSchema, {
    areaCode: bid.areaCode,
    timeCode: bid.timeCode,
    direction: bid.direction,
    ...(bid.fee?.kind === 'adjusted'
      ? priceInputOf(false, bid.fee.desiredPriceYenPerKwh, bid.fee.confirmed)
      : priceInputOf(false, bid.priceYenPerKwh, bid.fee?.confirmed)),
    volumeMwh: create(DecimalSchema, { value: bid.volumeMwh }),
    issuerType,
    origin,
    memoJepx: emptyToUndefined(memos?.memoJepx),
    memoFree: emptyToUndefined(memos?.memoFree),
  })
}

/**
 * 明細 Get が未解決 (loading / 一時失敗) のまま新規 Create しようとした時に
 * persistBids が投げる sentinel。 未解決の request に DRAFT が含まれるかもしれず
 * 「既存 DRAFT へ append か新規 Create か」 (AC-079) を確定できないため、
 * 重複申請セットの起票を防ぐ目的で書込を中断する。
 */
export class SpotDetailUnresolvedError extends Error {
  constructor() {
    super('spot approval request details are not resolved yet')
    this.name = 'SpotDetailUnresolvedError'
  }
}

/**
 * 入札一覧を 1 つの申請セットに絞り込み表示している間に、 その絞り込み対象の DRAFT が
 * 画面に出ていない状態で下書きへの書込 (札追加 / 一括作成 / CSV 取込 / 下書き復元) が
 * 走った時に persistBids が投げる sentinel。
 *
 * 該当するのは 2 つの契機の両方:
 *   - 承認前の札確認 (AC-BSP-094): 絞り込み先が PENDING なので DRAFT は最初から 0 件
 *   - 差戻し後の再編集 (AC-BSP-056): 絞り込み先の DRAFT が背景再取得で消えた
 *     (他ユーザーの操作・自分の別 tab での削除等。 解除は clearFocusIfMatches が行うが
 *     それはローカル操作経路でしか発火しない)
 *
 * 絞り込み中は表示と書込先を一致させるため DRAFT を絞り込み対象に限定している
 * (= 画面に出ていない DRAFT へ書き込まないため) が、 対象が解決できない状態は
 * 「DRAFT が無い」 ではなく「DRAFT が画面に出ていない」 なので、 そのまま Create 分岐へ
 * 落ちると既存 DRAFT を無視した重複申請セットを起票してしまう (受渡日 UNIQUE 撤廃後は
 * DB でも弾かれない)。 下書き復元は「全置換」 が「新規作成」 に化けて札が二重になる。
 * SpotDetailUnresolvedError と同じく fail-closed で中断する。
 */
export class SpotFocusedRequestUnavailableError extends Error {
  constructor() {
    super(
      'spot draft writes are blocked while the bid list is focused on a request whose draft is not shown',
    )
    this.name = 'SpotFocusedRequestUnavailableError'
  }
}

/**
 * 全 entry が operation-scoped (画面上部の Alert)。 どの理由も単一入力欄に 1:1 で
 * 帰属しない (締切 / 状態 / 階段数 / 二重提出は札単位ですらなく、 制度上限・
 * 入札制限違反は「どの札が原因か」が実行時に決まる) ため。
 */
const SPOT_WRITE_BLOCKED_REASON_KEYS: Record<
  SpotBidWriteBlockedReason,
  AppErrorInfoReasonEntry | undefined
> = {
  [SpotBidWriteBlockedReason.UNSPECIFIED]: undefined,
  [SpotBidWriteBlockedReason.GATE_CLOSURE]: {
    key: 'page:biddingSpot.serverError.reason.gateClosure',
  },
  [SpotBidWriteBlockedReason.NOT_DRAFT]: {
    key: 'page:biddingSpot.serverError.reason.notDraft',
  },
  [SpotBidWriteBlockedReason.INVALID_STATE_TRANSITION]: {
    key: 'page:biddingSpot.serverError.reason.invalidStateTransition',
  },
  [SpotBidWriteBlockedReason.JEPX_LIMIT_VIOLATION]: {
    key: 'page:biddingSpot.serverError.reason.jepxLimitViolation',
  },
  [SpotBidWriteBlockedReason.BID_LIMIT_VIOLATION]: {
    key: 'page:biddingSpot.serverError.reason.bidLimitViolation',
  },
  [SpotBidWriteBlockedReason.STAIRS_EXCEEDED]: {
    key: 'page:biddingSpot.serverError.reason.stairsExceeded',
  },
  [SpotBidWriteBlockedReason.ALREADY_SUBMITTED]: {
    key: 'page:biddingSpot.serverError.reason.alreadySubmitted',
  },
  [SpotBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION]: {
    key: 'page:biddingSpot.serverError.reason.bidVolumeTotalLimitViolation',
  },
  [SpotBidWriteBlockedReason.FEE_AUTO_CONSIDER_CHANGED]: {
    key: 'page:biddingSpot.serverError.reason.feeAutoConsiderChanged',
  },
  [SpotBidWriteBlockedReason.FEE_ADJUSTED_PRICE_OUT_OF_RANGE]: {
    key: 'page:biddingSpot.serverError.reason.feeAdjustedPriceOutOfRange',
  },
  // AC-BSP-117: 「下書きに戻す」 を押した時に送信 worker が送信の最中。 文言はブロックと同じ (#11944 Q2 の仮採用)。
  [SpotBidWriteBlockedReason.BID_SEND_IN_FLIGHT]: {
    key: 'page:biddingSpot.serverError.reason.bidSendInFlight',
  },
}

/**
 * FailedPrecondition に付いた `google.rpc.ErrorInfo` detail の reason から理由別
 * の文言を引く。 detail が無い / reason が未知 (= backend 先行デプロイ) /
 * registry entry が `undefined` の場合は undefined を返し、 呼び出し元が従来の
 * 汎用文言へフォールバックする。
 * CSV 取込の確定 (`SpotCsvImport`) も同じ文言を使う (#6095)。
 */
const SPOT_VOLUME_TOTAL_KEYS = {
  detail: 'page:biddingSpot.serverError.reason.bidVolumeTotalLimitViolation',
  plain:
    'page:biddingSpot.serverError.reason.bidVolumeTotalLimitViolationPlain',
}

export function spotWriteBlockedReasonMessage(
  err: ConnectError,
  t: TFunction,
): string | undefined {
  const info = readAppErrorInfo(err, SpotBidWriteBlockedReasonSchema)
  if (
    info?.reason === SpotBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION
  ) {
    return volumeTotalLimitMessage(info.metadata, t, SPOT_VOLUME_TOTAL_KEYS)
  }
  return resolveAppErrorInfoMessage(
    err,
    SpotBidWriteBlockedReasonSchema,
    SPOT_WRITE_BLOCKED_REASON_KEYS,
    t,
  )?.message
}

/**
 * 応答に載った `SpotBidWriteBlockedReason` (= ConnectError の ErrorInfo ではなく、 まとめて承認
 * (AC-BSP-118) が案件ごとに返す値) から単件エラーと同じ文言を引く。 registry を共有するので
 * 単件とまとめて承認で理由の文言が分かれない。 専用文言を持たない値は undefined。
 */
export function spotWriteBlockedReasonLabel(
  reason: SpotBidWriteBlockedReason,
  t: TFunction,
): string | undefined {
  if (reason === SpotBidWriteBlockedReason.BID_VOLUME_TOTAL_LIMIT_VIOLATION) {
    // まとめて承認の応答はエリア・コマ・合計を持たない。穴の空いた文は出さない。
    return t(SPOT_VOLUME_TOTAL_KEYS.plain)
  }
  const entry = SPOT_WRITE_BLOCKED_REASON_KEYS[reason]
  return entry === undefined ? undefined : t(entry.key)
}

/** Create/Update の ConnectError を画面表示用の日本語文言に訳す。 */
export function translateSpotWriteError(err: unknown, t: TFunction): string {
  if (err instanceof SpotDetailUnresolvedError) {
    return t('page:biddingSpot.serverError.detailUnresolved')
  }
  if (err instanceof SpotFocusedRequestUnavailableError) {
    return t('page:biddingSpot.serverError.focusedRequestUnavailable')
  }
  if (err instanceof ApprovalFlowUnresolvedError) {
    return t('page:biddingSpot.serverError.approvalFlowUnavailable')
  }
  if (err instanceof ConnectError) {
    switch (err.code) {
      case Code.Aborted:
        return t('page:biddingSpot.serverError.aborted')
      // AlreadyExists は同じ受渡日に承認依頼を複数持てる (AC-BSP-082) ため業務上は起きず、
      // 起きるのは uuid の主キー衝突だけ。 「その他の想定外エラー」 (AC-BSP-073) に寄せる。
      case Code.NotFound:
        return t('page:biddingSpot.serverError.notFound')
      case Code.InvalidArgument:
        return t('page:biddingSpot.serverError.invalidArgument')
      case Code.PermissionDenied:
        return t('page:biddingSpot.serverError.permissionDenied')
      case Code.FailedPrecondition:
        return (
          spotWriteBlockedReasonMessage(err, t) ??
          t('page:biddingSpot.serverError.failedPrecondition')
        )
      default:
        return t('page:biddingSpot.serverError.unknown')
    }
  }
  return t('page:biddingSpot.serverError.unknown')
}

/**
 * 下書きの承認申請・提出 (`TransitionBiddingSpotDrafts`) の ConnectError を画面表示用の文言に訳す。
 * 送信前に対象が下書きのままかを確かめているので、NOT_DRAFT は確認から送信までの間に他のユーザーが
 * 先に遷移させたことを表す。 notDraft の文言は編集・削除向け (「この札は編集できません…」) で
 * この操作には合わないため、競合 (Aborted) と同じ文言にする。 それ以外は `translateSpotWriteError` のまま。
 */
export function translateSpotDraftSubmitError(
  err: unknown,
  t: TFunction,
): string {
  if (
    err instanceof ConnectError &&
    err.code === Code.FailedPrecondition &&
    readAppErrorInfo(err, SpotBidWriteBlockedReasonSchema)?.reason ===
      SpotBidWriteBlockedReason.NOT_DRAFT
  ) {
    return t('page:biddingSpot.serverError.aborted')
  }
  return translateSpotWriteError(err, t)
}
